/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { get as cookieGet } from 'js/lib/cookie';
import { useRetracked } from 'js/lib/retracked';
import { isAuthenticatedUser, get as userGet } from 'js/lib/user';

import { MenuList, Popover } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import type { AccountDropdownLink } from 'bundles/composable-page-header/types';
import UserPortrait from 'bundles/page-header/components/UserPortraitV2';
import TrackedButton from 'bundles/page/components/TrackedButton';
import { getActionUrl } from 'bundles/user-account/common/lib';

import _t from 'i18n!nls/composable-page-header';

type Props = {
  dropdownLinks?: Array<AccountDropdownLink>;
  footer?: React.ReactNode;
  header?: React.ReactNode;
  isCourseraPlusLearner?: boolean;
};

const styles = {
  dropdownButton: css`
    &&& {
      background: none;
      border: none;
      padding: var(--cds-spacing-100);
      transition: background-color 0.3s ease;

      :hover {
        background-color: var(--cds-color-interactive-background-primary-hover-weak);
      }
    }
  `,
  linkItem: css`
    &:focus,
    &:hover {
      text-decoration: none;
    }
  `,
  logoutButton: css`
    height: 100%;
    width: 100%;
    background: none;
    text-align: left;
    border: none;
    padding: 0;

    &:focus {
      outline: none;
      border: none;
    }
  `,
  header: css`
    border-bottom: 1px solid var(--cds-color-grey-50);
    border-radius: var(--cds-border-radius-50);
    padding-bottom: var(--cds-spacing-100);
  `,
};

const AccountDropdownGridItemModule = ({ dropdownLinks, footer, header, isCourseraPlusLearner }: Props) => {
  const [popoverAnchor, setPopoverAnchor] = React.useState<HTMLButtonElement | null>(null);
  const logoutFormRef = React.useRef<HTMLFormElement>(null);
  const trackEvent = useRetracked();
  const trackV3Event = useTracker();

  const logoutFormKeyboardSubmit = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && logoutFormRef.current) {
      e.preventDefault();
      logoutFormRef.current.submit();
    }
  };

  const logoutFormClickSubmit = (e: React.MouseEvent) => {
    e.preventDefault();

    if (logoutFormRef.current) {
      logoutFormRef.current.submit();
    }
  };

  const linkItemClick = (e: React.MouseEvent, link: AccountDropdownLink) => {
    e.preventDefault();

    trackV3Event('click_profile_menu_item', {
      profileItemName: link.name ?? '',
      profileItemLink: link.href,
    });
    trackEvent({
      trackingName: `header_right_nav_button_${link.name.split('-').join('_')}`,
      action: 'click',
      trackingData: {
        name: link.name,
        href: link.href,
      },
    });

    window.location.href = link.href;
  };

  React.useEffect(() => {
    if (popoverAnchor) {
      trackV3Event('open_navigation_menu', {
        navigationType: 'profile',
      });
    }
  }, [popoverAnchor, trackV3Event]);

  if (!isAuthenticatedUser()) {
    return null;
  }

  return (
    <div id="authenticated-info-menu">
      <TrackedButton
        type="button"
        aria-label={_t('User dropdown menu for #{learnerName}', { learnerName: userGet().fullName })}
        onClick={(e) => setPopoverAnchor(e.currentTarget)}
        dataE2e="header-profile"
        trackingName="profile_drop_down_btn"
        css={styles.dropdownButton}
        tabIndex={0}
        aria-expanded={Boolean(popoverAnchor)}
      >
        <UserPortrait
          user={userGet()}
          showCaret
          isCaretUp={Boolean(popoverAnchor)}
          isCourseraPlusLearner={isCourseraPlusLearner}
        />
      </TrackedButton>
      <Popover open={Boolean(popoverAnchor)} anchorElement={popoverAnchor} onClose={() => setPopoverAnchor(null)}>
        {header && <Popover.Header css={styles.header}>{header}</Popover.Header>}
        <Popover.Body component="div" tabIndex={0}>
          <MenuList>
            {dropdownLinks?.map((link) => (
              <MenuList.ItemLink
                role="menuitem"
                href={link.href}
                key={link.name}
                target={link.openInNewWindow ? '_blank' : undefined}
                rel={link.openInNewWindow ? 'noopener noreferrer' : undefined}
                css={styles.linkItem}
                onClick={(e) => linkItemClick(e, link)}
              >
                {link.label}
              </MenuList.ItemLink>
            ))}
            <MenuList.Item role="menuitem" onClick={logoutFormClickSubmit} onKeyDown={logoutFormKeyboardSubmit}>
              <form
                role="none"
                action={getActionUrl('logout', cookieGet('CSRF3-Token') || '')}
                method="post"
                ref={logoutFormRef}
              >
                <TrackedButton trackingName="logout_btn" type="submit" css={styles.logoutButton} tabIndex={-1}>
                  {_t('Log Out')}
                </TrackedButton>
              </form>
            </MenuList.Item>
          </MenuList>
        </Popover.Body>
        {footer && <Popover.Footer>{footer}</Popover.Footer>}
      </Popover>
    </div>
  );
};

export default AccountDropdownGridItemModule;
